import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { AliyunCaptchaModal, type AliyunCaptchaResult } from '../components/AliyunCaptchaModal';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../context/AuthContext';
import { peekPreferFactoryLogin, peekWechatFactoryReturn } from '../lib/factoryContext';
import { authService } from '../services/authService';
import {
  buildCaptchaRequest,
  formatErrorMessage,
  isValidChinaPhone,
  normalizePhoneNumber,
  resolveCooldownSeconds,
} from '../utils/authFormUtils';

export function WechatOAuthBindPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bindToken = searchParams.get('bind_token')?.trim() || '';
  const { status } = useAuth();
  const factoryReturn =
    searchParams.get('intent') === 'factory'
    || peekWechatFactoryReturn()
    || peekPreferFactoryLogin();
  const afterBindTo = factoryReturn ? '/login?intent=factory' : '/';

  const [phone, setPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [captchaVisible, setCaptchaVisible] = useState(false);

  useEffect(() => {
    if (codeCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCodeCooldown((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [codeCooldown]);

  const handleSendCode = useCallback(() => {
    setError(null);
    setMessage(null);
    const normalizedPhone = normalizePhoneNumber(phone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setError('请输入正确的手机号');
      return;
    }
    setCaptchaVisible(true);
  }, [phone]);

  const handleCaptchaSuccess = useCallback(async (result: AliyunCaptchaResult) => {
    setCaptchaVisible(false);
    setSendingCode(true);
    setError(null);
    try {
      const normalizedPhone = normalizePhoneNumber(phone);
      const exists = await authService.doesPhoneNumberExist(normalizedPhone);
      const scene = exists ? 'signin' : 'signup';
      const response = await authService.verifyCaptchaAndRequestAuthCode(
        buildCaptchaRequest(result, normalizedPhone, scene),
      );
      setMessage(response.message);
      setCodeCooldown(resolveCooldownSeconds(response.expiresAt));
    } catch (err) {
      setError(formatErrorMessage(err, '发送验证码失败'));
    } finally {
      setSendingCode(false);
    }
  }, [phone]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const normalizedPhone = normalizePhoneNumber(phone);
      if (!isValidChinaPhone(normalizedPhone)) {
        throw new Error('请输入正确的手机号');
      }
      if (!authCode.trim()) {
        throw new Error('请输入短信验证码');
      }
      await authService.bindWechatWithPhone({
        bindToken,
        phone: normalizedPhone,
        authCode: authCode.trim(),
      });
      navigate(afterBindTo, { replace: true });
    } catch (err) {
      setError(formatErrorMessage(err, '验证失败'));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'signed-in') {
    return <Navigate to={afterBindTo} replace />;
  }

  if (!bindToken) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8FAFD] px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-card">
          <p className="text-sm text-danger">微信登录会话无效，请重新扫码。</p>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm font-medium text-primary hover:underline">返回登录页</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8FAFD] px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <h1 className="text-center text-xl font-bold text-ink">验证手机号</h1>
        <p className="mt-2 text-center text-sm leading-6 text-muted">
          微信首次登录需验证手机号。已注册用户将登录原账户并绑定微信；未注册用户将自动创建新账户。
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <label className="block text-sm">
            <span className="font-medium text-ink">手机号</span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="mt-2 w-full rounded-xl border border-border px-3 py-2.5"
              placeholder="请输入手机号"
              autoComplete="tel"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">短信验证码</span>
            <div className="mt-2 flex gap-2">
              <input
                value={authCode}
                onChange={(event) => setAuthCode(event.target.value)}
                className="flex-1 rounded-xl border border-border px-3 py-2.5"
                placeholder="6 位验证码"
                inputMode="numeric"
              />
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sendingCode || codeCooldown > 0}
                className="shrink-0 rounded-xl border border-primary px-3 text-sm font-medium text-primary disabled:opacity-50"
              >
                {sendingCode ? '验证中…' : codeCooldown > 0 ? `${codeCooldown}s` : '图形验证 · 发送'}
              </button>
            </div>
          </label>

          {error ? (
            <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">{error}</p>
          ) : null}
          {message ? (
            <p className="rounded-xl border border-[#CDE8D0] bg-[#F1FAF1] px-4 py-3 text-sm text-success">{message}</p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? '请稍候…' : '验证并继续'}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm font-medium text-primary hover:underline">返回登录页</Link>
          </div>
        </form>
      </div>

      <AliyunCaptchaModal
        visible={captchaVisible}
        onClose={() => setCaptchaVisible(false)}
        onError={(value) => setError(value)}
        onSuccess={(result) => void handleCaptchaSuccess(result)}
      />
    </div>
  );
}
